import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import ClientEditModal from "@/components/ClientEditModal";
import ClientDetailsModal from "@/components/ClientDetailsModal";
import { formatPhoneDisplay, formatPhoneForWhatsApp } from "@/utils/phoneFormat";
import { fmtNum } from "@/utils/toEnglishDigits";
import * as XLSX from "xlsx-js-style";
import {
  Users,
  UsersThree,
  Plus,
  Pencil,
  Trash,
  Phone,
  WhatsappLogo,
  MagnifyingGlass,
  DownloadSimple,
  Envelope,
  Note,
  UserPlus,
} from "@phosphor-icons/react";

/**
 * Company Clients page — self-service CRUD.
 * Uses the extended /company/clients API (UUID + phones[] + notes + stats).
 * Route:  /company/clients  (also aliased at /companies/clients)
 */
export default function CompanyClients() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [clients, setClients] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // {id?, initial?} — null=closed
  const [viewing, setViewing] = useState(null); // client being previewed

  // Redirect admins & drivers away — this page is company-only.
  useEffect(() => {
    if (user && (user.is_admin || user.role === "driver")) {
      navigate(user.is_admin ? "/admin/companies" : "/", { replace: true });
    }
  }, [user, navigate]);

  // Server-side search (debounced 300ms).
  const debounceRef = useRef();
  const load = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/company/clients", { params: query ? { q: query } : {} });
      setClients(Array.isArray(data?.items) ? data.items : []);
      setCount(Number(data?.count || 0));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(""); }, [load]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(q.trim()), 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, load]);

  // Save (create or edit) — hits the intercepted endpoints; the response
  // is always `{ok, pending, request_id}` for company users (mobile & web
  // share the same admin-approval workflow).
  const saveClient = async (payload) => {
    setBusy(true);
    try {
      if (editing?.id) {
        const { data } = await api.patch(`/company/clients/${editing.id}`, payload);
        if (data?.pending) {
          toast.success("⏳ تم إرسال طلب التعديل للإدارة");
        } else {
          toast.success("تم تحديث بيانات العميل");
          await load(q.trim());
        }
      } else {
        const { data } = await api.post("/company/clients", payload);
        if (data?.pending) {
          toast.success("⏳ تم إرسال طلب الإضافة للإدارة");
        } else {
          toast.success("تم إضافة العميل");
          await load(q.trim());
        }
      }
    } catch (err) {
      toast.error(formatApiError(err));
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const removeClient = async (client) => {
    const ok = await confirm({
      title: "حذف العميل",
      message: `هل أنت متأكد من حذف "${client.name}"؟ سيُرسل طلب الحذف للإدارة أولاً.`,
      confirmText: "نعم، أرسل طلب الحذف",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/company/clients/${client.id}`);
      if (data?.pending) {
        toast.success("⏳ تم إرسال طلب الحذف للإدارة");
      } else {
        toast.success("تم الحذف");
        await load(q.trim());
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // Excel bulk import removed (feature deprecated).

  const exportExcel = () => {
    if (!clients.length) return toast.error("لا يوجد عملاء للتصدير");
    const rows = [
      ["اسم العميل", "البريد الإلكتروني", "رقم الجوال الأول", "رقم الجوال الثاني", "ملاحظات"],
      ...clients.map((c) => [
        c.name || "",
        c.email || "",
        (c.phones && c.phones[0]) || "",
        (c.phones && c.phones[1]) || "",
        c.notes || "",
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 24 }, { wch: 28 }, { wch: 20 }, { wch: 20 }, { wch: 32 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "العملاء");
    XLSX.writeFile(wb, `clients-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("تم التصدير");
  };

  const openAdd = () => setEditing({ id: null, initial: null });
  const openEdit = (c) => setEditing({ id: c.id, initial: c });
  const openView = (c) => setViewing(c);

  return (
    <div className="min-h-screen bg-background text-foreground pb-16" dir="rtl">
      <TopBar />
      <main className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white inline-flex items-center gap-2">
              <UsersThree size={26} weight="duotone" className="text-primary" />
              عملائي
            </h1>
            <p className="text-white/50 text-sm mt-1">
              إدارة العملاء المتكررين لشركتك — إجمالي:{" "}
              <span className="num text-primary font-bold" dir="ltr" data-testid="clients-total">
                {fmtNum(count)}
              </span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={openAdd}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="add-client-btn"
            >
              <Plus size={13} weight="bold" /> عميل جديد
            </button>
            <button
              onClick={exportExcel}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-primary/40 text-primary hover:bg-primary/10 text-xs font-bold disabled:opacity-50"
              data-testid="export-excel-btn"
            >
              <DownloadSimple size={13} weight="bold" /> تصدير Excel
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlass size={16} weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث بالاسم أو الجوال أو البريد أو الملاحظات…"
            className="mz-input pr-10"
            data-testid="clients-search"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-16 text-primary animate-pulse" data-testid="clients-loading">
            جارٍ التحميل…
          </div>
        ) : clients.length === 0 && !q.trim() ? (
          <EmptyState onAdd={openAdd} />
        ) : clients.length === 0 ? (
          <div
            className="rounded-2xl bg-card border border-primary/15 p-10 text-center text-white/50"
            data-testid="clients-empty-search"
          >
            لا توجد نتائج مطابقة للبحث "{q}"
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
            data-testid="clients-grid"
          >
            {clients.map((c) => (
              <ClientCard
                key={c.id}
                client={c}
                onOpen={() => openView(c)}
                onEdit={() => openEdit(c)}
                onDelete={() => removeClient(c)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Details modal */}
      <ClientDetailsModal
        open={!!viewing}
        client={viewing}
        onClose={() => setViewing(null)}
        onEdit={(c) => {
          setViewing(null);
          setTimeout(() => openEdit(c), 60);
        }}
      />

      {/* Add / Edit modal */}
      <ClientEditModal
        open={!!editing}
        initial={editing?.initial}
        title={editing?.id ? "تعديل العميل" : "عميل جديد"}
        onSave={saveClient}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}

// ─── Empty state ───────────────────────────────────────────────────────
function EmptyState({ onAdd }) {
  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-[#0F2148] to-[#172956] border border-primary/25 p-10 md:p-14 text-center gold-glow"
      data-testid="clients-empty"
    >
      <div
        className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-5"
        style={{
          background: "radial-gradient(circle at 30% 30%, rgba(201,169,97,0.25) 0%, rgba(201,169,97,0.05) 70%)",
          border: "1px solid rgba(201,169,97,0.35)",
        }}
      >
        <UsersThree size={42} weight="duotone" className="text-primary" />
      </div>
      <h2 className="text-white text-xl md:text-2xl font-bold mb-2">
        لا يوجد عملاء بعد
      </h2>
      <p className="text-white/60 text-sm max-w-md mx-auto mb-6 leading-relaxed">
        أضِف عملاءك المتكررين لتسهيل إنشاء الحجوزات في المستقبل — البيانات محفوظة ومتاحة لك في أي وقت.
      </p>
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold text-sm shadow-[0_10px_30px_-10px_rgba(201,169,97,0.6)] transition-all"
        data-testid="add-first-client-btn"
      >
        <UserPlus size={16} weight="bold" /> إضافة أول عميل
      </button>
    </div>
  );
}

// ─── Client card ───────────────────────────────────────────────────────
function ClientCard({ client, onOpen, onEdit, onDelete }) {
  const phones = Array.isArray(client.phones) && client.phones.length > 0
    ? client.phones
    : [client.phone1, client.phone2].filter(Boolean);
  const initial = (client.name || "؟").trim().charAt(0).toUpperCase();

  return (
    <div
      className="rounded-2xl bg-card border border-primary/15 p-4 hover:border-primary/50 transition-all fadeup"
      data-testid={`client-card-${client.id}`}
    >
      {/* Top row: avatar + name/email */}
      <button
        type="button"
        onClick={onOpen}
        className="flex items-start gap-3 w-full text-right mb-3 group"
        data-testid={`client-open-${client.id}`}
      >
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center text-lg font-black text-primary-foreground shrink-0 group-hover:scale-105 transition-transform"
          style={{
            background: "linear-gradient(135deg,#C9A961 0%,#E1C484 100%)",
            boxShadow: "0 4px 12px rgba(201,169,97,0.30)",
          }}
          aria-hidden
        >
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-white font-bold text-base truncate group-hover:text-primary transition-colors">
            {client.name}
          </div>
          {client.email && (
            <div className="text-white/50 text-xs mt-0.5 inline-flex items-center gap-1 truncate" dir="ltr">
              <Envelope size={10} className="text-primary/50" /> {client.email}
            </div>
          )}
        </div>
      </button>

      {/* Phones */}
      {phones.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {phones.map((p, i) => {
            const digits = String(p).replace(/\D/g, "");
            const waHref = formatPhoneForWhatsApp(p);
            return (
              <div key={`${p}-${i}`} className="flex items-center justify-between gap-2" dir="ltr">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Phone size={11} weight={i === 0 ? "fill" : "regular"} className={i === 0 ? "text-primary/80" : "text-primary/40"} />
                  <span className={`num text-xs truncate ${i === 0 ? "text-white/90" : "text-white/60"}`}>
                    {formatPhoneDisplay(p)}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <a
                    href={`tel:+${digits}`}
                    onClick={(e) => e.stopPropagation()}
                    className="w-7 h-7 rounded-md border border-primary/30 text-primary hover:bg-primary/10 flex items-center justify-center"
                    title="اتصال"
                    data-testid={`client-tel-${client.id}-${i}`}
                  >
                    <Phone size={11} weight="bold" />
                  </a>
                  {waHref && (
                    <a
                      href={`https://wa.me/${waHref}?text=${encodeURIComponent(`مرحباً ${client.name || ""}،`)}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="w-7 h-7 rounded-md bg-[#25D366]/15 border border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366]/25 flex items-center justify-center"
                      title="واتساب"
                      data-testid={`client-wa-${client.id}-${i}`}
                    >
                      <WhatsappLogo size={11} weight="fill" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Notes preview (max 2 lines) */}
      {client.notes && (
        <div
          className="mb-3 text-white/70 text-[11px] flex items-start gap-1 leading-relaxed"
          style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
        >
          <Note size={11} weight="duotone" className="text-primary/60 shrink-0 mt-0.5" />
          <span>{client.notes}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t border-primary/10">
        <button
          onClick={onEdit}
          className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 rounded-lg border border-primary/30 text-primary hover:bg-primary/10 text-[11px] font-bold"
          data-testid={`edit-client-${client.id}`}
        >
          <Pencil size={11} weight="bold" /> تعديل
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 rounded-lg border border-destructive/40 text-destructive hover:bg-destructive/10"
          data-testid={`delete-client-${client.id}`}
          title="حذف"
        >
          <Trash size={11} weight="bold" />
        </button>
      </div>
    </div>
  );
}
